import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SocialProof = () => {
  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      company: "TechStart Inc.",
      role: "CEO",
      content: `bytepairs transformed our outdated website into a modern, high-converting platform. The team's attention to detail and technical expertise exceeded our expectations. Our online sales increased by 150% within the first month.`,
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      projectType: "E-commerce Redesign"
    },
    {
      id: 2,
      name: "Michael Chen",
      company: "GrowthLab",
      role: "Founder",
      content: `Working with bytepairs was a game-changer for our startup. They delivered a complex web application on time and under budget. The code quality is exceptional, and their ongoing support has been invaluable.`,
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      projectType: "Web Application"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      company: "Creative Agency Co.",
      role: "Creative Director",
      content: `The mobile app bytepairs built for us has been a huge success. Their user-centric approach and innovative solutions helped us stand out in a crowded market. Highly recommend their services!`,
      rating: 5,
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      projectType: "Mobile App Development"
    }
  ];

  const stats = [
    {
      icon: 'Rocket',
      value: 'Ready',
      label: 'To Build Your Vision',
      color: 'text-primary'
    },
    {
      icon: 'Code2',
      value: '15+',
      label: 'Technologies Mastered',
      color: 'text-success'
    },
    {
      icon: 'Zap',
      value: '100%',
      label: 'Commitment to Quality',
      color: 'text-warning'
    },
    {
      icon: 'Clock',
      value: '<2hrs',
      label: 'Response Time',
      color: 'text-accent'
    }
  ];

  const certifications = [
    { name: 'React Certified', icon: 'Code' },
    { name: 'AWS Partner', icon: 'Cloud' },
    { name: 'Google Analytics', icon: 'BarChart' },
    { name: 'Shopify Expert', icon: 'ShoppingBag' }
  ];

  return (
    <div className="space-y-12">
      {/* Stats Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="text-center"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name={stat.icon} size={24} className={stat.color} />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
            <div className="text-sm text-text-secondary">{stat.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Ready to Serve Section - Replacing Testimonials */}
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h3 className="text-2xl font-bold text-foreground mb-2">Ready to Deliver Excellence</h3>
          <p className="text-text-secondary">Fresh startup energy combined with technical expertise</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="card p-6 border-l-4 border-l-primary"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Target" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Quality First Approach</h4>
                <p className="text-text-secondary text-sm">
                  Every project receives our full attention and commitment to excellence. We're not juggling hundreds of clients - we focus on delivering premium quality for each partnership.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="card p-6 border-l-4 border-l-success"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Zap" size={24} color="var(--color-success)" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Fast & Agile</h4>
                <p className="text-text-secondary text-sm">
                  As a fresh startup, we're nimble and responsive. Static websites delivered in 1 day, full projects completed faster than traditional agencies.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="card p-6 border-l-4 border-l-accent"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="IndianRupee" size={24} color="var(--color-accent)" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Affordable Pricing</h4>
                <p className="text-text-secondary text-sm">
                  Competitive Indian market rates. Static sites from ₹15,000, web apps from ₹50,000. Enterprise quality without the enterprise price tag.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="card p-6 border-l-4 border-l-warning"
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-warning/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={24} color="var(--color-warning)" />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-2">Modern Tech Stack</h4>
                <p className="text-text-secondary text-sm">
                  15+ cutting-edge technologies mastered. React, Next.js, Node.js, Python, AI integration, and more. We build with the latest and best.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Certifications */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-gradient-to-r from-muted/50 to-surface/50 rounded-xl p-6"
      >
        <div className="text-center mb-6">
          <h4 className="text-lg font-semibold text-foreground mb-2">Certified & Trusted</h4>
          <p className="text-sm text-text-secondary">Our expertise is recognized by industry leaders</p>
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
              className="flex items-center space-x-2 bg-background rounded-lg px-4 py-2 border border-border"
            >
              <Icon name={cert.icon} size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">{cert.name}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Currently Accepting New Projects - COMMENTED OUT - Uncomment when ready to accept projects */}
      {/*
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="card p-6 border-l-4 border-l-success"
      >
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
            <Icon name="CheckCircle" size={24} color="var(--color-success)" />
          </div>
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-foreground mb-2">Currently Accepting New Projects</h4>
            <p className="text-text-secondary mb-3">
              We have availability for new projects starting in February 2025. Our team is ready to bring your vision to life.
            </p>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-text-secondary">3 spots available this month</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={14} color="var(--color-text-secondary)" />
                <span className="text-text-secondary">Next availability: Feb 15, 2025</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      */}
    </div>
  );
};

export default SocialProof;